import React from "react";
import {
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaFileAlt,
  FaUser,
  FaProjectDiagram,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaCheck,
  FaLightbulb,
} from "react-icons/fa";
import { getStatusBadge, getAssigneeInfo } from "./CalendarCallCard";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatTime = (dateString, item = null) => {
  if (dateString) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const isMidnightUTC =
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0;
      if (!isMidnightUTC) {
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${ampm}`;
      }
    }
  }

  // Fallback to item.Time
  if (item && item.Time) return item.Time;

  // Fallback to ObjectId creation time
  if (item) {
    const idStr = String(item._id || item.id || "");
    if (idStr.length === 24) {
      try {
        const timeFromId = parseInt(idStr.substring(0, 8), 16) * 1000;
        if (!isNaN(timeFromId) && timeFromId > 0) {
          const d = new Date(timeFromId);
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          return `${hours}:${minutes} ${ampm}`;
        }
      } catch (e) {}
    }
  }

  return "";
};

const calculateDuration = (startDate, endDate, status) => {
  if ((status || "").toLowerCase() !== "completed") return null;
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start) || isNaN(end) || end < start) return null;
  const diffMs = end - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

  const parts = [];
  if (diffDays > 0) parts.push(`${diffDays}d`);
  if (diffHrs > 0) parts.push(`${diffHrs}h`);
  if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
  return parts.join(" ");
};

const DateNavigationTaskCard = ({ call, onClick }) => {
  if (!call) return null;

  const statusBadge = getStatusBadge(call.Status);
  const assigneeInfo = getAssigneeInfo(call);

  const projectName =
    call.Project?.project_name ||
    call.Project?.ProjectName ||
    (typeof call.Project === "string" ? call.Project : "");

  const displayReq =
    (call.Requirement || "").trim() ||
    (call.PresentStatus || "").trim() ||
    "-";

  const dateFormatted = formatDate(call.Date);
  const timeFormatted = formatTime(call.Date, call);
  const duration = calculateDuration(call.Date, call.Complate_Date, call.Status);

  const isCompleted = (call.Status || "").toLowerCase() === "completed";

  return (
    <div
      onClick={() => onClick && onClick(call)}
      className="group relative rounded-xl border border-slate-200 bg-white hover:border-[#154D75] hover:shadow-md transition-all duration-200 overflow-hidden cursor-pointer flex flex-col justify-between"
    >
      {/* Top Accent Strip based on status */}
      <div
        className={`h-1 w-full ${statusBadge.stripBg || (isCompleted ? "bg-emerald-500" : "bg-blue-500")}`}
      />

      <div className="p-3.5 sm:p-4 flex flex-col flex-1 gap-3">
        {/* Row 1: Call No + Project Tag + Status Badge */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-300 shadow-2xs">
              <span className="text-xs font-semibold text-slate-500">Call No:</span>
              <span className="text-xs sm:text-sm font-bold text-slate-900 tracking-wide">
                {call.CallNo || "N/A"}
              </span>
            </div>

            {projectName && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase px-2 py-0.5 rounded-md bg-blue-50 text-[#154D75] border border-blue-200"
                title={`Project: ${projectName}`}
              >
                <FaProjectDiagram className="text-[10px]" />
                <span className="truncate max-w-[160px] sm:max-w-[220px]">{projectName}</span>
              </span>
            )}

            {call.Priority && call.Priority.toLowerCase() === "on" && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                Priority
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold text-xs border ${statusBadge.bg}`}
            >
              {statusBadge.icon}
              <span>{statusBadge.text}</span>
            </span>
          </div>
        </div>

        {/* Row 2: Requirement / Issue Details */}
        <div className="p-3 rounded-lg bg-slate-50/90 border border-slate-200/90 group-hover:bg-blue-50/20 group-hover:border-blue-200/70 transition-colors">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 mb-1">
            <FaFileAlt className="text-[#154D75] text-[11px]" />
            <span>REQ / Task Details:</span>
          </div>
          <p className="text-xs sm:text-[13px] text-slate-700 leading-relaxed break-words whitespace-pre-line font-normal">
            {displayReq}
          </p>
        </div>

        {/* Row 3: Solution (if available) */}
        {call.Solution && (
          <div className="p-2.5 rounded-lg bg-emerald-50/80 border border-emerald-200 text-emerald-950 text-xs">
            <div className="flex items-center gap-1.5 font-bold text-emerald-900 mb-0.5">
              <FaLightbulb className="text-emerald-700 text-[11px]" />
              <span>Solution:</span>
            </div>
            <p className="leading-relaxed break-words whitespace-pre-line text-emerald-900">
              {call.Solution}
            </p>
          </div>
        )}

        {/* Row 4: Key Info Metadata Grid (Completed By, Date & Time) */}
        <div className="pt-2.5 border-t border-slate-200/90 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {/* Completed By / Assignee */}
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
              <FaUser className="text-[10px]" />
            </div>
            <div className="truncate">
              <span className="text-slate-500 font-medium">
                {assigneeInfo.label || "Completed By"}:{" "}
              </span>
              <span className="font-bold text-slate-900">
                {assigneeInfo.name || "Not Assigned"}
              </span>
              {assigneeInfo.role && (
                <span className="text-[10px] text-slate-500 font-normal ml-1">
                  ({assigneeInfo.role})
                </span>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-center gap-1.5 min-w-0 sm:justify-end">
            <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
              <FaCalendarAlt className="text-[10px]" />
            </div>
            <div className="truncate text-slate-700">
              <span className="text-slate-500 font-medium">Date &amp; Time: </span>
              <span className="font-semibold text-slate-900">{dateFormatted}</span>
              {timeFormatted && (
                <span className="text-slate-600 ml-1">({timeFormatted})</span>
              )}
              {duration && (
                <span className="ml-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                  ⏱ {duration}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Hover Action Hint Bar */}
      <div className="px-3.5 py-1.5 bg-slate-50 border-t border-slate-200/80 flex items-center justify-between text-[11px] text-slate-500 group-hover:bg-[#154D75] group-hover:text-white transition-colors">
        <span className="font-medium">Click to inspect full call history &amp; records</span>
        <span className="inline-flex items-center gap-1 font-semibold">
          <span>View Details</span>
          <FaExternalLinkAlt className="text-[9px]" />
        </span>
      </div>
    </div>
  );
};

export default DateNavigationTaskCard;
